import { useCallback, type ComponentProps } from "react";
import * as Sentry from "@sentry/react";
import { isAxiosError } from "axios";
import { useUpdateTask } from "@api/task.api";
import { Task, TaskStatus } from "@common/types/task";
import { useTerminatingStatusGuard } from "@hooks/useTerminatingStatusGuard";
import type { TaskStatusTerminationConfirmationModal } from "./TaskStatusTerminationConfirmationModal";

/**
 * Updates a task's status from a status control, asking first when it would
 * finish a task that still has open blockers. Spread `confirmation` onto
 * `TaskStatusTerminationConfirmationModal`.
 *
 * `handleStatusChange` never rejects. A refused update, such as a view-only
 * user's, already shows the server's error toast; any other failure is sent
 * to Sentry.
 */
export const useTaskStatusChange = (task: Task) => {
    const { mutateAsync } = useUpdateTask(task);
    const updateStatus = useCallback(
        (status: TaskStatus) =>
            mutateAsync({ status }).catch((error) => {
                if (!isAxiosError(error) || !error.response) {
                    Sentry.captureException(error);
                }
            }),
        [mutateAsync],
    );
    const guard = useTerminatingStatusGuard(task, updateStatus);

    return {
        handleStatusChange: guard.handleStatusChange,
        confirmation: {
            pendingStatus: guard.pendingStatus,
            activeBlockerCount: guard.activeBlockerCount,
            onConfirm: guard.confirmChange,
            onCancel: guard.cancelChange,
            showDontAskAgain: guard.showDontAskAgain,
            shouldSkipConfirmation: guard.shouldSkipConfirmation,
            onShouldSkipConfirmationChange: guard.setShouldSkipConfirmation,
        } satisfies ComponentProps<
            typeof TaskStatusTerminationConfirmationModal
        >,
    };
};
